import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  MapPin,
  Briefcase,
  Calendar,
  Linkedin,
  Github,
  Globe,
  UserPlus,
  UserCheck,
  UserX,
  Clock,
  ArrowLeft,
  MessageCircle,
} from "lucide-react";
import { getOrCreateConversation } from "@/lib/messaging";

interface Profile {
  id: string;
  full_name: string;
  bio: string;
  location_city: string;
  experience_years: number;
  skills: string[];
  date_of_birth: string;
  linkedin_url: string;
  github_url: string;
  website_url: string;
}

interface Connection {
  id: string;
  requester_id: string;
  recipient_id: string;
  status: "pending" | "accepted" | "declined";
}

export default function ProfileView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [id]);

  const loadProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        return;
      }

      setCurrentUserId(session.user.id);

      if (session.user.id === id) {
        navigate("/edit-profile");
        return;
      }

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", id)
        .single();

      if (profileError) throw profileError;
      setProfile(profileData);

      // Check connection status
      const { data: connectionData } = await supabase
        .from("connections")
        .select("*")
        .or(`and(requester_id.eq.${session.user.id},recipient_id.eq.${id}),and(requester_id.eq.${id},recipient_id.eq.${session.user.id})`)
        .maybeSingle();

      setConnection(connectionData);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to load profile",
        variant: "destructive",
      });
      navigate("/connect");
    } finally {
      setIsLoading(false);
    }
  };

  const startConversation = async () => {
    try {
      // Check if conversation already exists
      const { data: existingParticipants } = await supabase
        .from("conversation_participants")
        .select("conversation_id")
        .eq("user_id", currentUserId);

      if (existingParticipants) {
        for (const participant of existingParticipants) {
          const { data: otherParticipant } = await supabase
            .from("conversation_participants")
            .select("*")
            .eq("conversation_id", participant.conversation_id)
            .eq("user_id", id)
            .single();

          if (otherParticipant) {
            navigate(`/messages/${participant.conversation_id}`);
            return;
          }
        }
      }

      // Create new conversation
      const { data: newConversation, error: conversationError } = await supabase
        .from("conversations")
        .insert({})
        .select()
        .single();

      if (conversationError) throw conversationError;

      // Add both participants
      const { error: participantsError } = await supabase
        .from("conversation_participants")
        .insert([
          { conversation_id: newConversation.id, user_id: currentUserId },
          { conversation_id: newConversation.id, user_id: id },
        ]);

      if (participantsError) throw participantsError;

      navigate(`/messages/${newConversation.id}`);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to start conversation",
        variant: "destructive",
      });
    }
  };

  const sendConnectionRequest = async () => {
    setActionLoading(true);
    try {
      const { error } = await supabase
        .from("connections")
        .insert({
          requester_id: currentUserId,
          recipient_id: id,
          status: "pending",
        });

      if (error) throw error;

      toast({
        title: "Request Sent",
        description: "Connection request sent successfully!",
      });

      loadProfile();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send connection request",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const respondToRequest = async (status: "accepted" | "declined") => {
    setActionLoading(true);
    try {
      const { error } = await supabase
        .from("connections")
        .update({ status })
        .eq("id", connection?.id);

      if (error) throw error;

      toast({
        title: status === "accepted" ? "Request Accepted" : "Request Declined",
        description: `Connection request ${status}!`,
      });

      loadProfile();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update connection request",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };


  const getConnectionButton = () => {
    if (!connection) {
      return (
        <div className="flex gap-2">
          <Button onClick={sendConnectionRequest} disabled={actionLoading} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Connect
          </Button>
          <Button onClick={startConversation} variant="outline" className="gap-2">
            Message
          </Button>
        </div>
      );
    }

    if (connection.status === "pending") {
      if (connection.requester_id === currentUserId) {
        return (
          <Button disabled className="gap-2">
            <Clock className="h-4 w-4" />
            Request Pending
          </Button>
        );
      } else {
        return (
          <div className="flex gap-2">
            <Button
              onClick={() => respondToRequest("accepted")}
              disabled={actionLoading}
              className="gap-2"
            >
              <UserCheck className="h-4 w-4" />
              Accept
            </Button>
            <Button
              onClick={() => respondToRequest("declined")}
              disabled={actionLoading}
              variant="outline"
              className="gap-2"
            >
              <UserX className="h-4 w-4" />
              Decline
            </Button>
          </div>
        );
      }
    }

    if (connection.status === "accepted") {
      return (
        <div className="flex gap-2">
          <Button onClick={startConversation} disabled={actionLoading} className="gap-2">
            <MessageCircle className="h-4 w-4" />
            Message
          </Button>
          <Button disabled variant="outline" className="gap-2">
            <UserCheck className="h-4 w-4" />
            Connected
          </Button>
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-24 text-center">
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-24 text-center">
          <p className="text-muted-foreground">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto px-4 py-24 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate("/connect")}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Directory
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center text-4xl font-bold text-primary">
                  {profile.full_name?.charAt(0) || "?"}
                </div>
                <div>
                  <CardTitle className="text-3xl mb-2">{profile.full_name || "Anonymous"}</CardTitle>
                  <div className="space-y-2">
                    {profile.location_city && (
                      <CardDescription className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {profile.location_city}
                      </CardDescription>
                    )}
                    {profile.experience_years !== null && (
                      <CardDescription className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        {profile.experience_years} years of experience
                      </CardDescription>
                    )}
                  </div>
                </div>
              </div>
              <div>{getConnectionButton()}</div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {profile.bio && (
              <div>
                <h3 className="text-lg font-semibold mb-2">About</h3>
                <p className="text-muted-foreground">{profile.bio}</p>
              </div>
            )}

            {profile.skills && profile.skills.length > 0 && (
              <>
                <Separator />
                <div>
                  <h3 className="text-lg font-semibold mb-3">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, index) => (
                      <Badge key={index} variant="secondary">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            {(profile.linkedin_url || profile.github_url || profile.website_url) && (
              <>
                <Separator />
                <div>
                  <h3 className="text-lg font-semibold mb-3">Links</h3>
                  <div className="space-y-2">
                    {profile.linkedin_url && (
                      <a
                        href={profile.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <Linkedin className="h-4 w-4" />
                        LinkedIn Profile
                      </a>
                    )}
                    {profile.github_url && (
                      <a
                        href={profile.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <Github className="h-4 w-4" />
                        GitHub Profile
                      </a>
                    )}
                    {profile.website_url && (
                      <a
                        href={profile.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <Globe className="h-4 w-4" />
                        Personal Website
                      </a>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
